import React, { PropsWithChildren } from 'react';
import styled, { css } from 'styled-components';
import { Icon } from '../icon';
import AngleLeft from '../icons/angle-left-solid.svg';
import AngleRight from '../icons/angle-right-solid.svg';

const defaultColorStyle = css<{ $type: TextButtonType }>`
    ${(props) =>
        props.$type === 'destructive'
            ? props.theme.app.component.textButton.destructive.default.color
            : props.theme.app.component.textButton.primary.default.color};
`;

const hoverColorStyle = css<{ $type: TextButtonType }>`
    ${(props) =>
        props.$type === 'destructive'
            ? props.theme.app.component.textButton.destructive.hover.color
            : props.theme.app.component.textButton.primary.hover.color};
`;

const activeColorStyle = css<{ $type: TextButtonType }>`
    ${(props) =>
        props.$type === 'destructive'
            ? props.theme.app.component.textButton.destructive.active.color
            : props.theme.app.component.textButton.primary.active.color};
`;

const Text = styled.span<{ $type: TextButtonType }>`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: ${defaultColorStyle};
    font-size: ${(props) => props.theme.app.component.textButton.fontSize};
    font-weight: ${(props) => (props.$type === 'destructive' ? '600' : props.theme.app.component.textButton.fontWeight)};
`;

const AngleLeftStyled = styled(AngleLeft)<{ $type: TextButtonType }>`
    fill: ${defaultColorStyle};
`;

const AngleRightStyled = styled(AngleRight)<{ $type: TextButtonType }>`
    fill: ${defaultColorStyle};
`;

const StyledIcon = styled(Icon)`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: ${(props) => props.theme.app.component.textButton.iconWidth};
    height: ${(props) => props.theme.app.component.textButton.iconHeight};
`;

const TextButtonWrapper = styled.span<{ $type: TextButtonType; $disabled?: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;

    ${(props) => props.$disabled && 'cursor: not-allowed; opacity: 0.45; pointer-events: none;'}

    &:hover {
        ${Text} {
            color: ${hoverColorStyle};
            ${(props) => props.$type === 'destructive' && 'text-decoration: underline;'}
        }
        ${AngleLeftStyled} {
            fill: ${hoverColorStyle};
        }
        ${AngleRightStyled} {
            fill: ${hoverColorStyle};
        }
    }

    &:active {
        ${Text} {
            color: ${activeColorStyle};
        }
        ${AngleLeftStyled} {
            fill: ${activeColorStyle};
        }
        ${AngleRightStyled} {
            fill: ${activeColorStyle};
        }
    }
`;

type IconPosition = 'unset' | 'left' | 'right';
type TextButtonType = 'primary' | 'destructive';

interface Props {
    className?: string;
    iconPosition?: IconPosition;
    type?: TextButtonType;
    disabled?: boolean;
    onClick?: (e: React.MouseEvent<HTMLSpanElement>) => void;
}

export const TextButton = ({ className, iconPosition = 'unset', type = 'primary', disabled, onClick, children }: PropsWithChildren<Props>) => {
    return (
        <TextButtonWrapper $type={type} className={className} onClick={disabled ? undefined : onClick} $disabled={disabled}>
            {iconPosition === 'left' && (
                <StyledIcon>
                    <AngleLeftStyled $type={type} />
                </StyledIcon>
            )}
            <Text $type={type}>{children}</Text>
            {iconPosition === 'right' && (
                <StyledIcon>
                    <AngleRightStyled $type={type} />
                </StyledIcon>
            )}
        </TextButtonWrapper>
    );
};
